import "./styles.less";

import type { PublicKey } from "@solana/web3.js";
import type { NormalizedTokenAccount } from "@stepfinance/tokens";
import type { FC } from "react";
import React, { useMemo, useState } from "react";

import { ReactComponent as BalanceIcon } from "../../../assets/balances.svg";
import { ReactComponent as StakeIcon } from "../../../assets/location.svg";
import { ReactComponent as SendIcon } from "../../../assets/mail.svg";
import { useAccountsContext, useAccountsContextRefresher } from "../../../contexts/accounts";
import { useLanguageContext } from "../../../contexts/language/language";
import { useMarkets } from "../../../contexts/market";
import type { CollapseControlProps } from "../../../hooks/home/useCollapseControl";
import { getTokenByMint } from "../../../utils/tokenInfo";
import { BurnModal } from "../../BurnModal/BurnModal";
import { Modal } from "../../Modal/Modal";
import { SendDomainContent } from "../../Send/SendDomainContent";
import { SendTokenContent } from "../../Send/SendTokenContent";
import type { StakeOption } from "../../Staking/StakingModal";
import { StakingModal } from "../../Staking/StakingModal";
import type { StakeAction } from "../../Staking/StakingTabs";
import { ValidatorStakingModal } from "../../Staking/ValidatorStakingModal";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";
import { Domains } from "./Domains";
import { SpotAssets } from "./SpotAssets";
import { YieldBearingAssets } from "./YieldBearingAssets";

export type StakeActionOption = {
  action: StakeAction;
  option: StakeOption;
};

export type UserDomain = {
  name: string;
  nameKey: PublicKey;
};

export const tokenSymbolSearchGetter = (record: NormalizedTokenAccount) => {
  const tokenInfo = getTokenByMint(record.info.mint);
  return tokenInfo ? tokenInfo.symbol : "";
};

export const Balances: FC<CollapseControlProps> = ({ activePanels, getPanelKey, onPanelClick }) => {
  const [sendAccount, setSendAccount] = useState<NormalizedTokenAccount | null>(null);
  const [burnAccount, setBurnAccount] = useState<NormalizedTokenAccount | null>(null);
  const [stakingOption, setStakingOption] = useState<StakeActionOption | null>(null);
  const [sendDomain, setSendDomain] = useState<UserDomain | null>(null);
  const {
    parsedUserTokenAccounts: {
      spotAssetTokenAccounts,
      yieldBearingTokenAccounts,
      spotAssetsBalanceUSD,
      yieldBearingAssetsBalanceUSD,
      userTokenAccountsBalanceUSD,
    },
  } = useAccountsContext();
  const refreshAccounts = useAccountsContextRefresher();
  const { marketsInitialised } = useMarkets();
  const hasPositions = useMemo(
    () => spotAssetTokenAccounts.length > 0 || yieldBearingTokenAccounts.length > 0,
    [spotAssetTokenAccounts, yieldBearingTokenAccounts],
  );

  const {
    currentLanguageData: {
      common,
      pages: {
        dashboard: {
          modules: { balances },
        },
      },
    },
  } = useLanguageContext();

  return (
    <>
      {hasPositions && marketsInitialised && (
        <div style={{ marginTop: "20px" }}>
          <ChevronCollapse
            activePanels={activePanels}
            title={common.balances}
            value={userTokenAccountsBalanceUSD}
            TitleIcon={BalanceIcon}
            panelKey={getPanelKey("Balances")}
            onChange={onPanelClick}
          >
            <>
              {spotAssetTokenAccounts.length > 0 && (
                <SpotAssets
                  userTokens={spotAssetTokenAccounts}
                  spotAssetsBalanceUSD={spotAssetsBalanceUSD}
                  setStakingOption={setStakingOption}
                  setSendAccount={setSendAccount}
                  setBurnAccount={setBurnAccount}
                />
              )}
              {yieldBearingTokenAccounts.length > 0 && (
                <YieldBearingAssets
                  yieldBearingTokens={yieldBearingTokenAccounts}
                  yieldBearingAssetsBalanceUSD={yieldBearingAssetsBalanceUSD}
                  setStakingOption={setStakingOption}
                  setSendAccount={setSendAccount}
                  setBurnAccount={setBurnAccount}
                />
              )}
              <Domains setSendDomain={setSendDomain} />
            </>
          </ChevronCollapse>
        </div>
      )}
      <Modal
        TitleIcon={SendIcon}
        title={balances.sendButton}
        onClose={() => setSendAccount(null)}
        width={500}
        visible={!!sendAccount}
      >
        <SendTokenContent fromAccount={sendAccount} closeModal={() => setSendAccount(null)} />
      </Modal>
      <BurnModal
        onSuccess={refreshAccounts}
        onClose={() => setBurnAccount(null)}
        data={burnAccount}
        width={500}
        visible={!!burnAccount}
      />
      <Modal
        TitleIcon={SendIcon}
        title={common.transferOwnership}
        onClose={() => setSendDomain(null)}
        width={500}
        visible={!!sendDomain}
      >
        {sendDomain && <SendDomainContent domain={sendDomain} closeModal={() => setSendDomain(null)} />}
      </Modal>
      <ValidatorStakingModal
        onClose={() => setStakingOption(null)}
        width={500}
        visible={stakingOption?.action === "stake"}
        stakeAction="stake"
      />
      <Modal
        TitleIcon={StakeIcon}
        title={common.unstake}
        onClose={() => setStakingOption(null)}
        width={500}
        visible={stakingOption?.action === "unstake"}
      >
        <StakingModal
          stakeOption={stakingOption?.option}
          stakeAction="unstake"
          closeModal={() => setStakingOption(null)}
        />
      </Modal>
    </>
  );
};
